import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Copy, Trash2, FileText, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Modal from '@/components/shared/Modal';
import { Program } from './types/program-types';

interface ProgramPlannerSubheaderProps {
    program: Program;
    currentDayIndex: number;
    onUpdate: (updater: (draft: Program) => void) => void;
    onAddDay: () => void;
    onDuplicateDay: (dayIndex: number) => void;
    onClearDay: (dayIndex: number) => void;
    onDeleteDay: (dayIndex: number) => void;
    onSetCurrentDay: (dayIndex: number) => void;
    rightColumns?: number;
    onSelectTemplate?: (tpl: 'blank' | 'conor' | 'custom1') => void;
    selectedTemplate?: 'blank' | 'conor' | 'custom1';
}

const ProgramPlannerSubheader: React.FC<ProgramPlannerSubheaderProps> = ({
    program,
    currentDayIndex,
    onUpdate,
    onAddDay,
    onDuplicateDay,
    onClearDay,
    onDeleteDay,
    onSetCurrentDay,
    rightColumns = 3,
    onSelectTemplate,
    selectedTemplate = 'conor'
}) => {
    const [editingDayName, setEditingDayName] = useState<string | null>(null);
    const [tempDayName, setTempDayName] = useState('');
    const [tempDayNotes, setTempDayNotes] = useState('');
    const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);

    // Template management state
    const [isNewTemplateModalOpen, setIsNewTemplateModalOpen] = useState(false);
    const [isEditTemplateModalOpen, setIsEditTemplateModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');

    // Responsive styling based on column width
    const isCompact = rightColumns <= 2;
    const isVeryCompact = rightColumns === 1;

    const handleDayNameEdit = (dayIndex: number, currentName: string, currentNotes: string = '') => {
        setEditingDayName(dayIndex.toString());
        setTempDayName(currentName || `Day ${dayIndex + 1}`);
        setTempDayNotes(currentNotes);
        setIsEditNameModalOpen(true);
    };

    const handleDayNameSave = () => {
        if (tempDayName.trim() && editingDayName !== null) {
            const dayIndex = parseInt(editingDayName);
            onUpdate(draft => {
                if (draft.days[dayIndex]) {
                    draft.days[dayIndex].name = tempDayName.trim();
                    draft.days[dayIndex].notes = tempDayNotes.trim();
                }
            });
        }
        handleDayNameCancel();
    };

    const handleDayNameCancel = () => {
        setEditingDayName(null);
        setTempDayName('');
        setTempDayNotes('');
        setIsEditNameModalOpen(false);
    };

    // Template management handlers
    const handleNewTemplate = () => {
        setTemplateName('');
        setTemplateDescription('');
        setIsNewTemplateModalOpen(true);
    };

    const handleSaveNewTemplate = () => {
        if (!templateName.trim()) {
            alert('Please enter a template name');
            return;
        }
        // For now, show a message that this feature will save custom templates
        alert(`Template "${templateName}" will be saved. This feature stores custom slot configurations for future use.`);
        setIsNewTemplateModalOpen(false);
        setTemplateName('');
        setTemplateDescription('');
    };

    const handleEditTemplate = () => {
        if (selectedTemplate === 'blank' || selectedTemplate === 'conor') {
            alert('Cannot edit built-in templates. Create a custom template instead.');
            return;
        }
        setTemplateName('Custom Template 1');
        setTemplateDescription('');
        setIsEditTemplateModalOpen(true);
    };

    const handleSaveEditTemplate = () => {
        if (!templateName.trim()) {
            alert('Please enter a template name');
            return;
        }
        alert(`Template "${templateName}" updated successfully.`);
        setIsEditTemplateModalOpen(false);
        setTemplateName('');
        setTemplateDescription('');
    };

    const handleDeleteTemplate = () => {
        if (selectedTemplate === 'blank' || selectedTemplate === 'conor') {
            alert('Cannot delete built-in templates.');
            return;
        }
        setIsDeleteConfirmOpen(true);
    };

    const confirmDeleteTemplate = () => {
        alert(`Template deleted. Switching to default template.`);
        if (onSelectTemplate) {
            onSelectTemplate('conor');
        }
        setIsDeleteConfirmOpen(false);
    };


    return (
        <div className={`bg-muted/30 border-b border-border ${isVeryCompact ? 'p-2' : isCompact ? 'p-3' : 'p-4'} h-full flex flex-col`}>
            {/* Day controls - first line */}
            <div className="flex items-center gap-2 flex-shrink-0">
                {/* Previous Day */}
                <Button
                    onClick={() => onSetCurrentDay(Math.max(0, currentDayIndex - 1))}
                    disabled={currentDayIndex === 0}
                    variant="ghost"
                    size="sm"
                    className={`${isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'}`}
                    title="Previous day"
                >
                    <ChevronLeft className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>

                {/* Day Label - Clickable to Edit */}
                <Button
                    onClick={() => handleDayNameEdit(currentDayIndex, program.days[currentDayIndex]?.name || `Day ${currentDayIndex + 1}`, program.days[currentDayIndex]?.notes || '')}
                    variant="ghost"
                    className={`${isVeryCompact ? 'text-xs' : isCompact ? 'text-sm' : 'text-base'} font-semibold flex-1 text-left justify-start hover:bg-muted/50`}
                    title="Click to edit day name and notes"
                >
                    {program.days[currentDayIndex]?.name || `Day ${currentDayIndex + 1}`}
                </Button>

                {/* Next Day */}
                <Button
                    onClick={() => onSetCurrentDay(Math.min(program.days.length - 1, currentDayIndex + 1))}
                    disabled={currentDayIndex >= program.days.length - 1}
                    variant="ghost"
                    size="sm"
                    className={`${isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'}`}
                    title="Next day"
                >
                    <ChevronRight className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>

                {/* Add Day */}
                <Button
                    onClick={onAddDay}
                    variant="default"
                    size="sm"
                    className={`${isVeryCompact ? 'h-6 px-2' : isCompact ? 'h-7 px-3' : 'h-8 px-3'}`}
                    title="Add new day"
                >
                    <Plus className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>

                {/* Duplicate Day */}
                <Button
                    onClick={() => onDuplicateDay(currentDayIndex)}
                    variant="ghost"
                    size="sm"
                    className={`${isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'}`}
                    title="Duplicate day"
                >
                    <Copy className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>

                {/* Clear Day */}
                <Button
                    onClick={() => onClearDay(currentDayIndex)}
                    variant="ghost"
                    size="sm"
                    className={`${isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'}`}
                    title="Clear day"
                >
                    <RotateCcw className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>

                {/* Delete Day */}
                {program.days.length > 1 && (
                    <Button
                        onClick={() => onDeleteDay(currentDayIndex)}
                        variant="ghost"
                        size="sm"
                        className={`${isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'} text-destructive hover:text-destructive`}
                        title="Delete day"
                    >
                        <Trash2 className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                    </Button>
                )}
            </div>

            {/* Second Line: Templates dropdown and controls */}
            <div className="flex items-center gap-2 flex-shrink-0 mt-2">
                <Select value={selectedTemplate} onValueChange={(v) => onSelectTemplate && onSelectTemplate(v as any)}>
                    <SelectTrigger className={`${isVeryCompact ? 'h-7' : isCompact ? 'h-8' : 'h-9'} flex-1`}>
                        <SelectValue placeholder="Templates" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="blank">_Blank</SelectItem>
                        <SelectItem value="conor">Conor Harris Methodology</SelectItem>
                        <SelectItem value="custom1">Custom 1</SelectItem>
                    </SelectContent>
                </Select>
                <Button 
                    onClick={handleNewTemplate}
                    size="sm" 
                    variant="outline" 
                    title="Save current day as new template" 
                    className={isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'}
                >
                    <Plus className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>
                <Button 
                    onClick={handleEditTemplate}
                    size="sm" 
                    variant="outline" 
                    title="Edit template" 
                    className={isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'}
                >
                    <FileText className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>
                <Button 
                    onClick={handleDeleteTemplate}
                    size="sm" 
                    variant="destructive" 
                    title="Delete template" 
                    className={isVeryCompact ? 'h-6 w-6 p-0' : isCompact ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0'}
                >
                    <Trash2 className={isVeryCompact ? 'h-3 w-3' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                </Button>
            </div>

            {/* Edit Day Name and Notes Modal */}
            <Modal
                isOpen={isEditNameModalOpen}
                onClose={handleDayNameCancel}
                title="Edit Day Details"
            >
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            Day Name
                        </label>
                        <Input
                            type="text"
                            value={tempDayName}
                            onChange={(e) => setTempDayName(e.target.value)}
                            placeholder="Enter day name..."
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            Notes
                        </label>
                        <Textarea
                            value={tempDayNotes}
                            onChange={(e) => setTempDayNotes(e.target.value)}
                            placeholder="Add notes for this day..."
                            rows={4}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button onClick={handleDayNameCancel} variant="outline">
                        Cancel
                    </Button>
                    <Button onClick={handleDayNameSave} variant="default">
                        Save
                    </Button>
                </div>
            </Modal>

            {/* New Template Modal */}
            <Modal
                isOpen={isNewTemplateModalOpen}
                onClose={() => setIsNewTemplateModalOpen(false)}
                title="Save as New Template"
            >
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            Template Name *
                        </label>
                        <Input
                            type="text"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="e.g., Upper Body Focus"
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            Description
                        </label>
                        <Textarea
                            value={templateDescription}
                            onChange={(e) => setTemplateDescription(e.target.value)}
                            placeholder="Describe this template..."
                            rows={3}
                            className="w-full"
                        />
                    </div>
                    <div className="bg-muted/50 p-3 rounded-lg text-sm text-muted-foreground">
                        <p className="font-medium mb-1">What gets saved:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Current day's slot structure</li>
                            <li>Slot labels (e.g., "Main Lift - Lower Push")</li>
                            <li>Empty slots ready for drill assignment</li>
                        </ul>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button onClick={() => setIsNewTemplateModalOpen(false)} variant="outline">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveNewTemplate} variant="default">
                        Save Template
                    </Button>
                </div>
            </Modal>

            {/* Edit Template Modal */}
            <Modal
                isOpen={isEditTemplateModalOpen}
                onClose={() => setIsEditTemplateModalOpen(false)}
                title="Edit Template"
            >
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            Template Name *
                        </label>
                        <Input
                            type="text"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="Template name"
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            Description
                        </label>
                        <Textarea
                            value={templateDescription}
                            onChange={(e) => setTemplateDescription(e.target.value)}
                            placeholder="Describe this template..."
                            rows={3}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button onClick={() => setIsEditTemplateModalOpen(false)} variant="outline">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveEditTemplate} variant="default">
                        Update Template
                    </Button>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                title="Delete Template?"
            >
                <div className="space-y-4">
                    <p className="text-foreground">
                        Are you sure you want to delete this template? This action cannot be undone.
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Template: <span className="font-medium">{selectedTemplate}</span>
                    </p>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button onClick={() => setIsDeleteConfirmOpen(false)} variant="outline">
                        Cancel
                    </Button>
                    <Button onClick={confirmDeleteTemplate} variant="destructive">
                        Delete Template
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default ProgramPlannerSubheader;
